import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Tag, Modal, message, Divider, Space } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMinusSquare } from '@fortawesome/free-regular-svg-icons';
import { faCheck, faTimes } from '@fortawesome/free-solid-svg-icons';
import { parseISO, format } from 'date-fns';
import { connect } from 'react-redux';
import Rest from 'tools/rest';
import {
  formatQuantityWithUnit,
  materialDisplayUnit,
  normalizeApproved,
  parseMaterialIntegerQty,
  availableQtyFloor,
  isQuantityExplicitZero,
} from '../utils';
import Preloader from 'components/preloader';
import TaskSearchModal from './taskSearchModal';
import IntegerQtyInput from './integerQtyInput';
import ReturnToWarehouseModal from './returnToWarehouseModal';

const APPROVED_COLORS = { pending: 'orange', approved: 'green', rejected: 'red' };
const APPROVED_LABELS = { pending: 'Ожидает', approved: 'Подтверждён', rejected: 'Отклонён' };

const MyMaterials = ({ currentUser, dataVersion, onDataChange, onPendingCountChange }) => {
  const [inventory, setInventory] = useState([]);
  const [pendingMoves, setPendingMoves] = useState([]);
  const [loading, setLoading] = useState(false);

  const [taskModalVisible, setTaskModalVisible] = useState(false);
  const [consumeItem, setConsumeItem] = useState(null);
  const [consumeTask, setConsumeTask] = useState(null);
  const [consumeQty, setConsumeQty] = useState(null);
  const [consumeModalVisible, setConsumeModalVisible] = useState(false);

  const [returnItem, setReturnItem] = useState(null);
  const [returnModalVisible, setReturnModalVisible] = useState(false);

  useEffect(() => {
    if (currentUser) {
      loadInventory();
      loadPending();
    }
  }, [currentUser, dataVersion]);

  const loadInventory = (opts = {}) => {
    const { silent = false } = opts;
    if (!silent) setLoading(true);
    Rest.get('/api/v1/inventories/my.json')
      .then((r) => setInventory(r.data.inventories || []))
      .catch(() => {})
      .finally(() => {
        if (!silent) setLoading(false);
      });
  };

  const loadPending = () => {
    Rest.get('/api/v1/material_moves/pending.json')
      .then((r) => {
        const items = r.data.material_moves || [];
        setPendingMoves(items);
        onPendingCountChange(items.length);
      })
      .catch(() => {});
  };

  const refreshAll = useCallback((opts = {}) => {
    loadInventory(opts);
    loadPending();
  }, []);

  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === 'visible') refreshAll({ silent: true });
    };
    const onFocus = () => refreshAll({ silent: true });
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', onFocus);
    const t = setInterval(() => refreshAll({ silent: true }), 30_000);
    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('focus', onFocus);
      clearInterval(t);
    };
  }, [refreshAll]);

  const handleApprove = (moveId) => {
    Rest.put(`/api/v1/material_moves/${moveId}/approve.json`)
      .then(() => { message.success('Принято'); onDataChange(); })
      .catch((err) => message.error(err.response?.data?.error || 'Ошибка'));
  };

  const handleReject = (moveId) => {
    Modal.confirm({
      title: 'Отклонить перемещение?',
      okText: 'Отклонить',
      cancelText: 'Отмена',
      okButtonProps: { danger: true },
      onOk: () =>
        Rest.put(`/api/v1/material_moves/${moveId}/reject.json`)
          .then(() => { message.success('Отклонено'); onDataChange(); })
          .catch((err) => message.error(err.response?.data?.error || 'Ошибка')),
    });
  };

  const handleStartConsume = (item) => {
    if (parseFloat(item.quantity) <= 0) {
      message.error('Нет материала в наличии');
      return;
    }
    setConsumeItem(item);
    setConsumeTask(null);
    setTaskModalVisible(true);
  };

  const handleTaskSelected = (task) => {
    setConsumeTask(task);
    setTaskModalVisible(false);
    setConsumeModalVisible(true);
  };

  const handleConfirmConsume = () => {
    if (isQuantityExplicitZero(consumeQty)) {
      message.error('Количество должно быть больше 0');
      return;
    }
    const q = parseMaterialIntegerQty(consumeQty);
    if (q === null) {
      message.error('Укажите целое количество не меньше 1');
      return;
    }
    const available = availableQtyFloor(consumeItem);
    if (q > available) {
      message.error(
        `Недостаточно (доступно: ${formatQuantityWithUnit(available, materialDisplayUnit(consumeItem))})`
      );
      return;
    }

    setLoading(true);
    Rest.post('/api/v1/material_moves.json', {
      material_move: {
        source_type: 'Inventory',
        source_user_id: currentUser.id,
        material_id: consumeItem.material_id,
        destination_type: 'Request',
        destination_id: consumeTask.id,
        quantity: q,
      },
    })
      .then(() => {
        message.success('Материал списан на задачу');
        setConsumeModalVisible(false);
        setConsumeItem(null);
        setConsumeTask(null);
        onDataChange();
      })
      .catch((err) => {
        message.error(err.response?.data?.errors?.join(', ') || 'Ошибка списания');
      })
      .finally(() => setLoading(false));
  };

  const pendingColumns = [
    { title: 'Материал', dataIndex: 'material_name' },
    {
      title: 'Кол-во',
      dataIndex: 'quantity',
      width: '12%',
      render: (qty, record) => formatQuantityWithUnit(qty, materialDisplayUnit(record)),
    },
    {
      title: 'Статус', dataIndex: 'approved', width: '10%',
      render: (s) => {
        const key = normalizeApproved(s);
        return <Tag color={APPROVED_COLORS[key]}>{APPROVED_LABELS[key]}</Tag>;
      },
    },
    {
      title: 'Дата', dataIndex: 'created_at', width: '15%',
      render: (d) => d ? format(parseISO(d), 'dd.MM.yyyy HH:mm') : '—',
    },
    {
      title: 'Действия', width: '18%',
      render: (_, record) => (
        <div style={{ display: 'flex', gap: 8 }}>
          <Button
            type="primary"
            size="small"
            icon={<FontAwesomeIcon icon={faCheck} />}
            onClick={() => handleApprove(record.id)}
          >
            Принять
          </Button>
          <Button
            danger
            size="small"
            icon={<FontAwesomeIcon icon={faTimes} />}
            onClick={() => handleReject(record.id)}
          >
            Отклонить
          </Button>
        </div>
      ),
    },
  ];

  const inventoryColumns = [
    { title: 'Материал', dataIndex: 'material_name' },
    {
      title: 'Категория',
      dataIndex: 'category_name',
      render: (v) => v || '—',
    },
    {
      title: 'Кол-во',
      dataIndex: 'quantity',
      width: '12%',
      render: (qty, record) => formatQuantityWithUnit(qty, materialDisplayUnit(record)),
    },
    {
      title: 'Действие', width: '12%',
      render: (_, record) => (
        <div style={{ display: 'flex', gap: 8 }}>
          <Button
            size="small"
            title="Списать на задачу"
            icon={<FontAwesomeIcon icon={faMinusSquare} />}
            onClick={() => handleStartConsume(record)}
          />
          <Button
            size="small"
            title="Вернуть на склад"
            onClick={() => {
              setReturnItem(record);
              setReturnModalVisible(true);
            }}
          >
            Вернуть
          </Button>
        </div>
      ),
    },
  ];

  return (
    <Preloader loading={loading}>
      {pendingMoves.length > 0 && (
        <>
          <Table
            columns={pendingColumns}
            dataSource={pendingMoves}
            rowKey="id"
            size="small"
            pagination={false}
          />
          <Divider />
        </>
      )}

      <Table
        columns={inventoryColumns}
        dataSource={inventory}
        rowKey="id"
        size="small"
        pagination={false}
        locale={{
          emptyText: 'Пусто. Остаток появится после перемещения материала со склада к вам (и приёма, если склад без автоподтверждения).',
        }}
      />

      <TaskSearchModal
        visible={taskModalVisible}
        onClose={() => {
          setTaskModalVisible(false);
          if (!consumeModalVisible) {
            setConsumeItem(null);
          }
        }}
        onSelectTask={handleTaskSelected}
      />

      {consumeModalVisible && (
        <Modal
          title={`Списать: ${consumeItem?.material_name || ''}`}
          open={consumeModalVisible}
          onOk={handleConfirmConsume}
          onCancel={() => {
            setConsumeModalVisible(false);
            setConsumeItem(null);
            setConsumeTask(null);
            setConsumeQty(null);
          }}
          okText="Списать"
          cancelText="Отмена"
        >
          <div style={{ marginBottom: 8, padding: '8px 12px', backgroundColor: '#f5f5f5', borderRadius: 4 }}>
            <strong>Задача #{consumeTask?.id}:</strong> {consumeTask?.description || '—'}
          </div>
          <Button
            type="link"
            size="small"
            onClick={() => setTaskModalVisible(true)}
            style={{ paddingLeft: 0, marginBottom: 16, height: 'auto' }}
          >
            Выбрать другую задачу
          </Button>
          <p>
            Доступно:{' '}
            <strong>
              {formatQuantityWithUnit(consumeItem?.quantity, materialDisplayUnit(consumeItem))}
            </strong>
          </p>
          <Space align="center" style={{ width: '100%' }} wrap>
            <IntegerQtyInput
              value={consumeQty}
              onChange={setConsumeQty}
              style={{ width: 160 }}
              placeholder="Только целое число"
            />
            {materialDisplayUnit(consumeItem) ? (
              <span style={{ color: '#888' }}>{materialDisplayUnit(consumeItem)}</span>
            ) : null}
          </Space>
        </Modal>
      )}

      {returnModalVisible && returnItem && (
        <ReturnToWarehouseModal
          visible={returnModalVisible}
          item={returnItem}
          currentUser={currentUser}
          onClose={() => {
            setReturnModalVisible(false);
            setReturnItem(null);
          }}
          onSuccess={() => onDataChange()}
        />
      )}
    </Preloader>
  );
};

const mapStateToProps = (state) => ({ currentUser: state.user });
export default connect(mapStateToProps)(MyMaterials);
